import { useCallback, useState } from 'react';

type MutationStatus = 'idle' | 'loading' | 'success' | 'error';

export interface MutationOptions<TData, TVariables> {
  onMutate?: (variables: TVariables) => void;
  onSuccess?: (data: TData) => void;
  onError?: (error: any) => void;
  onSettled?: (data: TData | null, error: any) => void;
}

interface MutationResult<TData, TVariables> {
  status: MutationStatus;
  data: TData | null;
  error: any;
  mutate: (variables: TVariables) => void;
  mutateAsync: (variables: TVariables) => Promise<TData>;
}

export const useMutation = <TData = void, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: MutationOptions<TData, TVariables>
): MutationResult<TData, TVariables> => {
  const [status, setStatus] = useState<MutationStatus>('idle');
  const [data, setData] = useState<TData | null>(null);
  const [error, setError] = useState<any>(null);

  const mutateAsync = useCallback(
    async (variables: TVariables): Promise<TData> => {
      if (options?.onMutate) {
        options.onMutate(variables);
      }
      setStatus('loading');
      setData(null);
      setError(null);

      try {
        const result = await mutationFn(variables);
        setData(result);
        setStatus('success');
        if (options?.onSuccess) {
          options.onSuccess(result);
        }
        if (options?.onSettled) {
          options.onSettled(result, null);
        }
        return result;
      } catch (err) {
        setError(err);
        setStatus('error');
        if (options?.onError) {
          options.onError(err);
        }
        if (options?.onSettled) {
          options.onSettled(null, err);
        }
        throw err; // re-throw the error to allow user to handle it
      }
    },
    [mutationFn, options]
  );

  const mutate = useCallback(
    (variables: TVariables) => {
      mutateAsync(variables).catch(() => {
        // Error handling is already done in mutateAsync
      });
    },
    [mutateAsync]
  );

  return { status, data, error, mutate, mutateAsync };
};
